/**
 * Thin typed wrappers around the worker's /api/v1/* endpoints.
 * All calls include credentials so the tsp_session cookie rides along.
 */

export type Role = "user" | "admin" | "curator";

export interface User {
  id: string;
  email: string;
  role: Role;
  createdAt: number;
  lastSeenAt: number | null;
}

export interface TrailListItem {
  slug: string;
  name: string;
  region: string | null;
  state: string | null;
  country: string;
  latitude: number | null;
  longitude: number | null;
  statusPageUrl?: string;
  verifiedAt?: number | null;
  /** Multi-select activity tags assigned to the trail. Empty when
   *  no curator/admin has tagged it yet. Drives the catalog filter
   *  chip strip + the per-row activity chips. */
  activities?: string[];
  status: {
    value: "open" | "closed" | "partially_open" | "unknown" | null;
    summary: string | null;
    source: "cron" | "admin" | null;
    setAt: number | null;
  };
  /** Embedded org snapshot when the trail is linked to a real
   *  (user-created) organization. Auto-stubs from the C1 backfill
   *  come back as null so the row doesn't show noise like
   *  "Blankets Creek · Blankets Creek". */
  organization?: {
    id: string;
    slug: string;
    name: string;
    logoUrl: string | null;
  } | null;
}

export interface TrailDetail extends TrailListItem {
  statusPageUrl: string;
  websiteUrl: string | null;
  description: string | null;
  status: TrailListItem["status"] & { expiresAt: number | null };
  attributes: Record<string, { value: string; valueType: string }>;
  /** Always present on detail (vs optional on list rows). Multi-
   *  select activity tags for this trail. */
  activities: string[];
  /** Facebook-style header banner set by the trail crew. Null when
   *  no banner has been uploaded yet. */
  banner: { url: string; width: number; height: number } | null;
  /** Parent organization. The worker's public DTO carries this when
   *  the trail is linked; nil for unparented trails. Curator drawer
   *  reads this to seed the org picker. */
  organization: EmbeddedOrganization | null;
  revision: number;
}

export interface MyClaim {
  id: string;
  status: "pending" | "approved" | "denied";
  reason: string | null;
  createdAt: number;
  reviewedAt: number | null;
  trail: { slug: string; name: string };
}

export interface CuratorClaim {
  id: string;
  status: string;
  reason: string | null;
  reviewNote: string | null;
  createdAt: number;
  reviewedAt: number | null;
  requester: { id: string; email: string; name: string | null };
  trail: {
    slug: string;
    name: string;
    region: string | null;
    state: string | null;
    statusPageUrl: string;
  };
}

/** Trail-membership role. The platform-level `Role` covers
 *  user/admin/curator; THIS is the per-trail tier inside a single
 *  trail system. Phase B1 surfaces it via the admin assignment
 *  payload so the UI can show role pills and gate capabilities. */
export type MembershipRole = "owner" | "steward" | "member";

/** Lightweight org snapshot embedded inside trail responses. The
 *  full org profile is fetched via getOrganization() when needed. */
export interface EmbeddedOrganization {
  id: string;
  slug: string;
  name: string;
  logoUrl: string | null;
}

/** Adds `trailCount` so the curator launchpad can show the org's
 *  scale without an extra fetch. Same shape as EmbeddedOrganization
 *  plus the count. */
export interface OrgListRow extends EmbeddedOrganization {
  websiteUrl?: string | null;
  donationUrl?: string | null;
  trailCount: number;
}

export interface AdminAssignment {
  slug: string;
  name: string;
  region: string | null;
  state: string | null;
  country: string;
  /** The page the cron polls for this trail's status. */
  statusPageUrl: string;
  /** This admin's per-trail role. After the Phase B1 backfill every
   *  existing admin is `owner`; Phase B2 will start producing
   *  `steward` rows once invites ship. The list endpoint already
   *  filters out member-only memberships so callers won't see them
   *  here. */
  role: MembershipRole;
  /** Parent organization. Currently always present (the C1 backfill
   *  gave every trail an org); the type allows null so future
   *  unparented trails don't break the contract. */
  organization: EmbeddedOrganization | null;
  status: TrailListItem["status"] & { expiresAt: number | null };
}

// ---------------------------------------------------------------------------
// Organizations (Phase C1)
// ---------------------------------------------------------------------------

export interface OrganizationProfile {
  id: string;
  slug: string;
  name: string;
  logoUrl: string | null;
  websiteUrl: string | null;
  mission: string | null;
  contactEmail: string | null;
  instagramHandle: string | null;
  facebookUrl: string | null;
  stravaClubUrl: string | null;
  donationUrl: string | null;
  foundedAt: number | null;
  /** Federal EIN for 501(c)3 nonprofits (e.g. "12-3456789"). Plain
   *  string — UI handles XX-XXXXXXX format hints. */
  taxId: string | null;
  /** Multi-select activity tags for the org — the disciplines it
   *  stewards (e.g. SORBA Woodstock = ["mountain_biking"], a
   *  county parks dept might be ["hiking", "mountain_biking",
   *  "horseback"]). Empty when nothing's been tagged yet. */
  activities: string[];
  createdAt: number;
  updatedAt: number;
}

export interface OrgPosition {
  id: string;
  title: string;
  holderName: string | null;
  holderEmail: string | null;
  holderPhone: string | null;
  positionOrder: number;
}

export interface OrgWithTrails {
  organization: OrganizationProfile;
  trails: { slug: string; name: string; region: string | null; state: string | null }[];
  canEdit: boolean;
}

export function getOrganization(idOrSlug: string) {
  return req<OrgWithTrails>("GET", `/api/v1/admin/orgs/${encodeURIComponent(idOrSlug)}`);
}

export interface OrgEditInput {
  name?: string;
  slug?: string;
  logoUrl?: string | null;
  websiteUrl?: string | null;
  mission?: string | null;
  contactEmail?: string | null;
  instagramHandle?: string | null;
  facebookUrl?: string | null;
  stravaClubUrl?: string | null;
  donationUrl?: string | null;
  foundedAt?: number | null;
  taxId?: string | null;
  /** Replace-set semantics. Send the full desired list — the
   *  worker DELETEs every existing row then INSERTs the new set.
   *  Empty array clears all tags; omit to leave them alone. */
  activities?: string[];
}

export function updateOrganization(idOrSlug: string, patch: OrgEditInput) {
  return req<{ organization: OrganizationProfile; canEdit: boolean }>(
    "PUT",
    `/api/v1/admin/orgs/${encodeURIComponent(idOrSlug)}`,
    patch
  );
}

/** List the org's roster (President, VP, Treasurer, etc.). Any
 *  signed-in user can read (matches the org-profile pattern). */
export function listOrgPositions(idOrSlug: string) {
  return req<{ positions: OrgPosition[] }>(
    "GET",
    `/api/v1/admin/orgs/${encodeURIComponent(idOrSlug)}/positions`
  );
}

/** Replace the org's full roster atomically. Caller sends the
 *  ordered array of positions; worker DELETE+INSERTs in a D1 batch.
 *  Only Trail Boss (curator or owner) can write. */
export function replaceOrgPositions(
  idOrSlug: string,
  positions: Array<{
    title: string;
    holderName?: string | null;
    holderEmail?: string | null;
    holderPhone?: string | null;
    positionOrder?: number;
  }>
) {
  return req<{ positions: OrgPosition[] }>(
    "PUT",
    `/api/v1/admin/orgs/${encodeURIComponent(idOrSlug)}/positions`,
    { positions }
  );
}

/**
 * Upload a cropped + resized logo blob (PNG, 512×512) to R2 via the
 * worker. The body is the raw image — no multipart wrapping — so the
 * worker can stream it straight to R2 without parsing a form. The
 * server returns the public URL + the version timestamp (so the
 * client can cache-bust by appending `?v=...`).
 */
/**
 * Upload the cropped logo blob to R2 via the worker. Uses
 * XMLHttpRequest under the hood (not fetch) because fetch can't
 * report upload progress — and on a poor mountain mobile signal a
 * 2 MB POST can easily run 10+ seconds. The Doherty Threshold says
 * users disengage past ~400ms with no feedback; the onProgress
 * callback lets the UI draw a real progress bar.
 *
 * Also catches the failure modes specific to flaky networks
 * (offline / timeout / aborted) and surfaces them as distinct
 * messages rather than a generic "upload failed".
 */
export function uploadOrgLogo(
  orgId: string,
  blob: Blob,
  onProgress?: (percent: number) => void
): Promise<{ ok: true; logoUrl: string; version: number }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    // 60s ceiling — plenty for a 2 MB POST on a 200 Kbps signal.
    xhr.timeout = 60_000;
    xhr.responseType = "json";
    xhr.withCredentials = true;
    xhr.open(
      "POST",
      `/api/v1/admin/orgs/${encodeURIComponent(orgId)}/logo`,
      true
    );
    xhr.setRequestHeader("content-type", blob.type || "image/png");

    xhr.upload.onprogress = (event) => {
      if (!event.lengthComputable || !onProgress) return;
      const percent = Math.min(99, Math.round((event.loaded / event.total) * 100));
      onProgress(percent);
    };

    xhr.onload = () => {
      const body = xhr.response as
        | { ok?: true; logoUrl?: string; version?: number; error?: string }
        | null;
      if (xhr.status >= 200 && xhr.status < 300 && body?.ok && body.logoUrl) {
        // Snap to 100 once the server's response lands so the UI's
        // bar reaches a satisfying full state before celebrating.
        onProgress?.(100);
        resolve({
          ok: true,
          logoUrl: body.logoUrl,
          version: body.version ?? Math.floor(Date.now() / 1000),
        });
        return;
      }
      const msg = body?.error ?? `Upload failed (${xhr.status})`;
      reject(new ApiError(xhr.status, msg, body));
    };

    xhr.onerror = () => {
      reject(
        new ApiError(
          0,
          "Couldn't reach the server. Check your signal and try again.",
          null
        )
      );
    };
    xhr.ontimeout = () => {
      reject(
        new ApiError(
          0,
          "Upload took too long. Try again on a stronger signal.",
          null
        )
      );
    };
    xhr.onabort = () => {
      reject(new ApiError(0, "Upload cancelled.", null));
    };

    xhr.send(blob);
  });
}

/**
 * Upload a Facebook-style header banner for a trail. Same XHR /
 * progress / error-class pattern as uploadOrgLogo — the client has
 * already resized the image to ~1600×900 JPEG, dimensions ride as
 * headers so the worker can persist them without parsing pixels.
 */
export function uploadTrailBanner(
  slug: string,
  blob: Blob,
  width: number,
  height: number,
  onProgress?: (percent: number) => void
): Promise<{ ok: true; banner: { url: string; width: number; height: number }; version: number }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.timeout = 60_000;
    xhr.responseType = "json";
    xhr.withCredentials = true;
    xhr.open(
      "POST",
      `/api/v1/admin/trails/${encodeURIComponent(slug)}/banner`,
      true
    );
    xhr.setRequestHeader("content-type", blob.type || "image/jpeg");
    xhr.setRequestHeader("x-banner-width", String(width));
    xhr.setRequestHeader("x-banner-height", String(height));

    xhr.upload.onprogress = (event) => {
      if (!event.lengthComputable || !onProgress) return;
      const percent = Math.min(99, Math.round((event.loaded / event.total) * 100));
      onProgress(percent);
    };

    xhr.onload = () => {
      const body = xhr.response as
        | { ok?: true; banner?: { url: string; width: number; height: number }; version?: number; error?: string }
        | null;
      if (xhr.status >= 200 && xhr.status < 300 && body?.ok && body.banner) {
        onProgress?.(100);
        resolve({
          ok: true,
          banner: body.banner,
          version: body.version ?? Math.floor(Date.now() / 1000),
        });
        return;
      }
      const msg = body?.error ?? `Upload failed (${xhr.status})`;
      reject(new ApiError(xhr.status, msg, body));
    };
    xhr.onerror = () => reject(new ApiError(0, "Couldn't reach the server. Check your signal and try again.", null));
    xhr.ontimeout = () => reject(new ApiError(0, "Upload took too long. Try again on a stronger signal.", null));
    xhr.onabort = () => reject(new ApiError(0, "Upload cancelled.", null));

    xhr.send(blob);
  });
}

export function deleteTrailBanner(slug: string) {
  return req<{ ok: true }>(
    "DELETE",
    `/api/v1/admin/trails/${encodeURIComponent(slug)}/banner`
  );
}

// Per-event header image — exact mirror of uploadTrailBanner, hitting
// the event image endpoint with x-image-* dimension headers.
export function uploadEventImage(
  eventId: string,
  blob: Blob,
  width: number,
  height: number,
  onProgress?: (percent: number) => void
): Promise<{ ok: true; image: { url: string; width: number; height: number }; version: number }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.timeout = 60_000;
    xhr.responseType = "json";
    xhr.withCredentials = true;
    xhr.open(
      "POST",
      `/api/v1/admin/events/${encodeURIComponent(eventId)}/image`,
      true
    );
    xhr.setRequestHeader("content-type", blob.type || "image/jpeg");
    xhr.setRequestHeader("x-image-width", String(width));
    xhr.setRequestHeader("x-image-height", String(height));

    xhr.upload.onprogress = (event) => {
      if (!event.lengthComputable || !onProgress) return;
      const percent = Math.min(99, Math.round((event.loaded / event.total) * 100));
      onProgress(percent);
    };

    xhr.onload = () => {
      const body = xhr.response as
        | { ok?: true; image?: { url: string; width: number; height: number }; version?: number; error?: string }
        | null;
      if (xhr.status >= 200 && xhr.status < 300 && body?.ok && body.image) {
        onProgress?.(100);
        resolve({
          ok: true,
          image: body.image,
          version: body.version ?? Math.floor(Date.now() / 1000),
        });
        return;
      }
      const msg = body?.error ?? `Upload failed (${xhr.status})`;
      reject(new ApiError(xhr.status, msg, body));
    };
    xhr.onerror = () => reject(new ApiError(0, "Couldn't reach the server. Check your signal and try again.", null));
    xhr.ontimeout = () => reject(new ApiError(0, "Upload took too long. Try again on a stronger signal.", null));
    xhr.onabort = () => reject(new ApiError(0, "Upload cancelled.", null));

    xhr.send(blob);
  });
}

export function deleteEventImage(eventId: string) {
  return req<{ ok: true }>(
    "DELETE",
    `/api/v1/admin/events/${encodeURIComponent(eventId)}/image`
  );
}

// ---------------------------------------------------------------------------
// Trail GPX uploads
// ---------------------------------------------------------------------------
// One row per uploaded .gpx file. The browser parses the GPX once on
// file-select to derive metadata (length, gain, loss, bbox); the worker
// stores it verbatim. iOS + Android fetch the raw .gpx from the
// fileUrl path on demand and parse it themselves.

/** IMBA 5-tier difficulty rating + our custom "incredibly_difficult"
 *  red tier for trails beyond the IMBA ceiling. Mirror of the worker
 *  enum (worker/src/gpx.ts GPX_DIFFICULTIES). */
export type GpxDifficulty =
  | "easiest"
  | "easy"
  | "more_difficult"
  | "very_difficult"
  | "extremely_difficult"
  | "incredibly_difficult";

export interface GpxClientMetadata {
  lengthM: number | null;
  elevationGainM: number | null;
  elevationLossM: number | null;
  bbox: { north: number; south: number; east: number; west: number } | null;
  pointCount: number | null;
}

export interface GpxFileDto {
  id: string;
  name: string;
  difficulty: GpxDifficulty;
  /** Relative path served by the worker: /assets/trails/:slug/gpx/:id.gpx */
  fileUrl: string;
  lengthM: number | null;
  elevationGainM: number | null;
  elevationLossM: number | null;
  bbox: { north: number; south: number; east: number; west: number } | null;
  pointCount: number | null;
  createdAt: number;
  updatedAt: number;
}

export function listAdminGpx(slug: string) {
  return req<{ items: GpxFileDto[] }>(
    "GET",
    `/api/v1/admin/trails/${encodeURIComponent(slug)}/gpx`
  );
}

export function updateGpxFile(
  slug: string,
  id: string,
  patch: { name?: string; difficulty?: GpxDifficulty }
) {
  return req<{ item: GpxFileDto }>(
    "PUT",
    `/api/v1/admin/trails/${encodeURIComponent(slug)}/gpx/${encodeURIComponent(id)}`,
    patch
  );
}

export function deleteGpxFile(slug: string, id: string) {
  return req<{ ok: true }>(
    "DELETE",
    `/api/v1/admin/trails/${encodeURIComponent(slug)}/gpx/${encodeURIComponent(id)}`
  );
}

/**
 * Multipart upload of a single .gpx file with browser-derived metadata.
 * XHR (not fetch) so we get a real progress bar — GPX files run
 * 200KB–3MB and on a 200kbps signal that's >10s of upload time, well
 * past the Doherty threshold.
 */
export function uploadGpxFile(
  slug: string,
  file: File,
  params: {
    name: string;
    difficulty: GpxDifficulty;
    metadata: GpxClientMetadata;
  },
  onProgress?: (percent: number) => void
): Promise<{ item: GpxFileDto }> {
  return new Promise((resolve, reject) => {
    const form = new FormData();
    form.append("file", file, file.name);
    form.append("name", params.name);
    form.append("difficulty", params.difficulty);
    form.append("metadata", JSON.stringify(params.metadata));

    const xhr = new XMLHttpRequest();
    xhr.timeout = 90_000;
    xhr.responseType = "json";
    xhr.withCredentials = true;
    xhr.open(
      "POST",
      `/api/v1/admin/trails/${encodeURIComponent(slug)}/gpx`,
      true
    );

    xhr.upload.onprogress = (event) => {
      if (!event.lengthComputable || !onProgress) return;
      const percent = Math.min(99, Math.round((event.loaded / event.total) * 100));
      onProgress(percent);
    };

    xhr.onload = () => {
      const body = xhr.response as
        | { item?: GpxFileDto; error?: string }
        | null;
      if (xhr.status >= 200 && xhr.status < 300 && body?.item) {
        onProgress?.(100);
        resolve({ item: body.item });
        return;
      }
      const msg = body?.error ?? `Upload failed (${xhr.status})`;
      reject(new ApiError(xhr.status, msg, body));
    };

    xhr.onerror = () =>
      reject(
        new ApiError(
          0,
          "Couldn't reach the server. Check your signal and try again.",
          null
        )
      );
    xhr.ontimeout = () =>
      reject(
        new ApiError(0, "Upload took too long. Try again on a stronger signal.", null)
      );
    xhr.onabort = () => reject(new ApiError(0, "Upload cancelled.", null));

    xhr.send(form);
  });
}

class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    /** Full parsed JSON body when the server returned structured detail
     *  (e.g. a 409 conflict carrying a `conflicts` list). Use this in
     *  callers that need more than the human-readable message. */
    public body?: unknown
  ) {
    super(message);
  }
}

export async function req<T>(
  method: string,
  path: string,
  body?: unknown
): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: "include",
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });

  const text = await res.text();
  const data = text ? safeParse(text) : null;

  if (!res.ok) {
    const msg =
      (data && typeof data === "object" && "error" in data
        ? String((data as { error: unknown }).error)
        : null) ??
      text ??
      `HTTP ${res.status}`;
    throw new ApiError(res.status, msg, data);
  }
  return data as T;
}

export interface NameConflict {
  slug: string;
  name: string;
  region: string | null;
  state: string | null;
  statusPageUrl: string;
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export function requestMagicLink(email: string, redirectTo?: string) {
  return req<{ ok: true }>("POST", "/api/v1/auth/request-magic-link", {
    email,
    redirectTo,
  });
}

/**
 * PWA-friendly counterpart to the magic-link callback: the user pastes
 * the 6-digit code from their email instead of tapping the link. Cookie
 * gets set on this response, so it lands in the PWA's cookie container
 * directly (vs. Safari's, which is where a tapped-from-Mail link ends up).
 */
export function verifyCode(email: string, code: string) {
  return req<{ ok: true; user: User }>("POST", "/api/v1/auth/verify-code", {
    email,
    code,
  });
}

export async function getMe(): Promise<User | null> {
  try {
    const { user } = await req<{ user: User }>("GET", "/api/v1/me");
    return user;
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return null;
    throw err;
  }
}

export function logout() {
  return req<null>("POST", "/api/v1/auth/logout");
}

// ---------------------------------------------------------------------------
// Catalog (public)
// ---------------------------------------------------------------------------

export interface TrailListResponse {
  items: TrailListItem[];
  nextCursor: string | null;
}

export function listTrails(params: {
  q?: string;
  state?: string;
  prefix?: string;
  /** Single-select activity filter. Mirrors the worker's
   *  ?activity= EXISTS-clause filter. Unknown keys are coerced to
   *  empty (no filter) by the worker, never 500. */
  activity?: string;
  cursor?: string;
  limit?: number;
} = {}) {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.state) qs.set("state", params.state);
  if (params.prefix) qs.set("prefix", params.prefix);
  if (params.activity) qs.set("activity", params.activity);
  if (params.cursor) qs.set("cursor", params.cursor);
  if (params.limit) qs.set("limit", String(params.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return req<TrailListResponse>("GET", `/api/v1/trails${suffix}`);
}

export function getTrail(slug: string) {
  return req<{ trail: TrailDetail }>("GET", `/api/v1/trails/${slug}`);
}

// ---------------------------------------------------------------------------
// Admin (signed-in user with assignment)
// ---------------------------------------------------------------------------

export function listMyAdminTrails() {
  return req<{ items: AdminAssignment[] }>("GET", "/api/v1/admin/trails");
}

export function proposeTrail(input: {
  name: string;
  statusPageUrl: string;
  websiteUrl?: string;
  region?: string;
  state?: string;
  latitude?: number;
  longitude?: number;
  /** Optional list of amenity keys (e.g. "parking", "bathrooms") to
   *  enable on the new trail. Worker writes each as a
   *  `amenity.<key>` = "true" row in trail_attributes. Lets curators
   *  bootstrap amenities at create time instead of needing a
   *  follow-up edit. */
  amenities?: string[];
}) {
  return req<{ trail: TrailDetail; deduped: boolean }>(
    "POST",
    "/api/v1/admin/trails",
    input
  );
}

export function createClaim(slug: string, reason?: string) {
  return req<{ claim: { id: string; status: string; createdAt: number } }>(
    "POST",
    "/api/v1/admin/claims",
    { slug, reason }
  );
}

export function listMyClaims() {
  return req<{ items: MyClaim[] }>("GET", "/api/v1/admin/claims/mine");
}

export function setStatusOverride(input: {
  slug: string;
  status: "open" | "closed" | "partially_open" | "unknown";
  summary?: string;
  ttlHours?: number;
}) {
  return req<{
    ok: true;
    trail: { slug: string; name: string };
    status: {
      value: string;
      summary: string | null;
      source: "admin";
      setAt: number;
      setBy: string;
      expiresAt: number;
    };
    pushed: number;
  }>("POST", "/api/v1/admin/status", input);
}

export function clearStatusOverride(slug: string) {
  return req<{ ok: true }>("DELETE", `/api/v1/admin/status/${slug}`);
}

// ---------------------------------------------------------------------------
// Admin — crew (Phase B2)
// ---------------------------------------------------------------------------

export interface CrewMember {
  userId: string;
  email: string;
  role: MembershipRole;
  joinedAt: number;
  invitedBy: string | null;
  /** True when this row is the signed-in user — the UI uses this to
   *  disable self-revoke (a Trail Boss can't remove themselves; they
   *  have to promote someone else first). */
  isYou: boolean;
}

export function listMyCrew(slug: string) {
  return req<{ items: CrewMember[] }>(
    "GET",
    `/api/v1/admin/trails/${encodeURIComponent(slug)}/crew`
  );
}

export function inviteCrewMember(
  slug: string,
  email: string,
  role: "steward" | "member" = "steward"
) {
  return req<{
    ok: true;
    userId: string;
    email: string;
    role?: MembershipRole;
    alreadyOnCrew?: boolean;
  }>(
    "POST",
    `/api/v1/admin/trails/${encodeURIComponent(slug)}/crew`,
    { email, role }
  );
}

export function removeCrewMember(slug: string, userId: string) {
  return req<{ ok: true }>(
    "DELETE",
    `/api/v1/admin/trails/${encodeURIComponent(slug)}/crew/${encodeURIComponent(userId)}`
  );
}

// ---------------------------------------------------------------------------
// Events (Phase B3)
// ---------------------------------------------------------------------------

export type EventType = "work_party" | "group_ride" | "race" | "trail_day" | "other";
export type RsvpStatus = "going" | "maybe" | "not_going";

export interface TrailEvent {
  id: string;
  trailSystemId: string;
  title: string;
  description: string | null;
  eventType: EventType;
  startsAt: number;
  endsAt: number | null;
  location: string | null;
  capacity: number | null;
  createdBy: string;
  createdByEmail: string;
  createdAt: number;
  cancelledAt: number | null;
  /** Phase B4.1 — how many times the host has triggered an
   *  announcement push for this event. Hard-capped at 3 server-side. */
  announcementCount: number;
  /** Unix seconds of the most recent announce, or null when the
   *  event has never been announced. */
  lastAnnouncedAt: number | null;
  rsvps: { going: number; maybe: number; not_going: number };
  myRsvp: RsvpStatus | null;
  /** Per-event header image (mirrors the trail banner). Null until a
   *  host uploads one. */
  image: { url: string; width: number; height: number } | null;
}

export interface CreateEventInput {
  title: string;
  description?: string;
  eventType?: EventType;
  /** Unix seconds. */
  startsAt: number;
  endsAt?: number;
  location?: string;
  capacity?: number;
}

export function listTrailEvents(slug: string) {
  return req<{ items: TrailEvent[] }>(
    "GET",
    // /calendar (not /events) — the /events route is the existing
    // audit log endpoint that iOS uses for the trail timeline.
    `/api/v1/trails/${encodeURIComponent(slug)}/calendar`
  );
}

export function createTrailEvent(slug: string, input: CreateEventInput) {
  return req<{ ok: true; event: TrailEvent }>(
    "POST",
    `/api/v1/admin/trails/${encodeURIComponent(slug)}/events`,
    input
  );
}

export function updateTrailEvent(eventId: string, patch: Partial<CreateEventInput>) {
  return req<{ ok: true; event: TrailEvent }>(
    "PUT",
    `/api/v1/admin/events/${encodeURIComponent(eventId)}`,
    patch
  );
}

export function cancelTrailEvent(
  eventId: string,
  audience: "all" | "rsvps_only" = "all"
) {
  return req<{
    ok: true;
    event: TrailEvent;
    pushed: number;
    audience: "all" | "rsvps_only";
  }>(
    "DELETE",
    `/api/v1/admin/events/${encodeURIComponent(eventId)}`,
    { audience }
  );
}

/**
 * Soft-archive an event — hides it from the admin trail card + the
 * rider-facing list, but keeps the row + RSVPs in D1 for the future
 * analytics dashboard (attendance, volunteer hours). Distinct from
 * cancel which pushes riders; archive is silent host hygiene.
 * Requires host_events on the trail.
 */
export function archiveTrailEvent(eventId: string) {
  return req<{ ok: true }>(
    "POST",
    `/api/v1/admin/events/${encodeURIComponent(eventId)}/archive`
  );
}

/**
 * Phase B4.1 — fire an announcement push for an event. Server gates:
 *   - host_events capability
 *   - per-event cap (3)
 *   - per-host 24h cap (5)
 * Returns the updated event with bumped counters + how many devices
 * the push actually landed on.
 */
export function announceTrailEvent(eventId: string) {
  return req<{
    ok: true;
    event: TrailEvent;
    pushed: number;
    sequence: number;
    capRemaining: number;
  }>(
    "POST",
    `/api/v1/admin/events/${encodeURIComponent(eventId)}/announce`
  );
}

export function rsvpToEvent(eventId: string, status: RsvpStatus) {
  return req<{ ok: true; status: RsvpStatus }>(
    "POST",
    `/api/v1/events/${encodeURIComponent(eventId)}/rsvp`,
    { status }
  );
}

export function clearRsvp(eventId: string) {
  return req<{ ok: true }>(
    "DELETE",
    `/api/v1/events/${encodeURIComponent(eventId)}/rsvp`
  );
}

export function setTrailAttribute(
  slug: string,
  key: string,
  value: string,
  valueType: "string" | "number" | "boolean" | "json"
) {
  return req<{ ok: true }>("PUT", `/api/v1/admin/trails/${slug}/attributes`, {
    key,
    value,
    valueType,
  });
}

export function deleteTrailAttribute(slug: string, key: string) {
  return req<{ ok: true; removed: number }>(
    "DELETE",
    `/api/v1/admin/trails/${slug}/attributes/${encodeURIComponent(key)}`
  );
}

// ---------------------------------------------------------------------------
// Curator
// ---------------------------------------------------------------------------

export function listCuratorClaims(status: "pending" | "approved" | "denied" = "pending") {
  return req<{ items: CuratorClaim[] }>("GET", `/api/v1/curator/claims?status=${status}`);
}

export function approveClaim(claimId: string, note?: string) {
  return req<{ ok: true }>("POST", `/api/v1/curator/claims/${claimId}/approve`, { note });
}

export function denyClaim(claimId: string, note?: string) {
  return req<{ ok: true }>("POST", `/api/v1/curator/claims/${claimId}/deny`, { note });
}

export function drainCandidates() {
  return req<{ ok: true; promoted: number; skipped: number; promotedSlugs: string[] }>(
    "POST",
    "/api/v1/curator/drain-candidates"
  );
}

// --- Comp codes (free Pro grants) ------------------------------------------

export interface CompCode {
  code: string;
  grantType: string;
  maxRedemptions: number | null;
  redeemedCount: number;
  expiresAt: number | null;
  note: string | null;
  createdAt: number;
}

export function listCompCodes() {
  return req<{ items: CompCode[] }>("GET", "/api/v1/curator/comp-codes");
}

export function createCompCode(input: {
  code?: string;
  maxRedemptions?: number | null;
  expiresAt?: number | null;
  note?: string;
}) {
  return req<CompCode>("POST", "/api/v1/curator/comp-codes", input);
}

export function deleteCompCode(code: string) {
  return req<null>("DELETE", `/api/v1/curator/comp-codes/${encodeURIComponent(code)}`);
}

export interface BackfillCollision {
  key: string;
  winner: { id: string; slug: string; name: string };
  loser: { id: string; slug: string; name: string; state: string | null };
}

/** The persistent pairing surfaced after backfill. `keeper` is the
 *  row currently holding the canonical_locality_key; `pending` is the
 *  row that's still unstamped and would have collided if we tried.
 *  Returned regardless of when the collision was first detected,
 *  unlike the per-run-only `collisions` field. */
export interface BackfillDuplicate {
  key: string;
  keeper: { id: string; slug: string; name: string; state: string | null };
  pending: { id: string; slug: string; name: string; state: string | null };
}

export function backfillCanonicalKeys() {
  return req<{
    ok: true;
    scanned: number;
    updated: number;
    duplicates: BackfillDuplicate[];
    collisions: BackfillCollision[];
    conflictedAtWrite: Array<{ id: string; name: string; key: string }>;
  }>("POST", "/api/v1/curator/backfill-canonical-keys");
}

/** Collapse `loserSlug` into `winnerSlug`. Moves trail_events,
 *  events, attributes, memberships, activities onto the winner, then
 *  deletes the loser row. Audit row is logged on the winner. */
export function mergeTrails(winnerSlug: string, loserSlug: string) {
  return req<{
    ok: true;
    mergedInto: { slug: string; name: string };
    removed: { slug: string; name: string };
  }>("POST", "/api/v1/curator/merge-trails", { winnerSlug, loserSlug });
}

/** Tell the dedup spine this row really IS distinct from its near-
 *  twin. Stamps the row with a forced-unique canonical key so the
 *  UNIQUE constraint no longer collides. */
export function markTrailDistinct(slug: string) {
  return req<{ ok: true; canonicalLocalityKey: string }>(
    "POST",
    `/api/v1/curator/trails/${slug}/mark-distinct`
  );
}

export function verifyTrail(slug: string) {
  return req<{ ok: true }>("POST", `/api/v1/curator/trails/${slug}/verify`);
}

export function unverifyTrail(slug: string) {
  return req<{ ok: true }>("DELETE", `/api/v1/curator/trails/${slug}/verify`);
}

export interface TrailEditInput {
  name?: string;
  region?: string | null;
  state?: string | null;
  country?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  statusPageUrl?: string;
  websiteUrl?: string | null;
  description?: string | null;
  /** Curator-grade: link or unlink the parent organization. Pass an
   *  org id to assign, null to detach. The worker validates the id
   *  resolves before committing. */
  organizationId?: string | null;
  /** Replace-set semantics. Send the full desired list — the worker
   *  DELETEs every existing row then INSERTs the new set. Empty
   *  array clears all tags; omit to leave them alone. */
  activities?: string[];
}

export function editTrail(slug: string, input: TrailEditInput) {
  return req<{ trail: TrailDetail }>(
    "PUT",
    `/api/v1/curator/trails/${slug}`,
    input
  );
}

/**
 * Lightweight list of every organization for the curator's
 * "assign org to trail" picker AND the launchpad management card.
 * Returns trailCount per org so the dashboard can show scale.
 * Curator-only.
 */
export function listOrganizationsForCurator() {
  return req<{ organizations: OrgListRow[] }>(
    "GET",
    "/api/v1/curator/orgs"
  );
}

/**
 * Create a new organization (just a name). The slug auto-generates
 * from the name with collision suffixes (e.g. "sorba-woodstock-2").
 * Returns the full org shape so callers can immediately link a trail
 * to it. Curator-only.
 */
export function createOrganization(name: string) {
  return req<{ organization: EmbeddedOrganization }>(
    "POST",
    "/api/v1/curator/orgs",
    { name }
  );
}

/** Per-org analytics rollup. `null` fields are parked metrics that
 *  need new schema (attendance, volunteer hours, donations,
 *  Member-level tenure). UI renders "Coming soon" empty states. */
export interface OrgAnalytics {
  organization: { id: string; slug: string; name: string; logoUrl: string | null };
  metrics: {
    eventsCount: number;
    eventsCancelled: number;
    eventsArchived: number;
    rsvpTotals: { going: number; maybe: number; notGoing: number };
    trailsCount: number;
    crewCount: number;
    crewTenureAvgDays: number | null;
    pushReach: number;
    attendanceTotal: number | null;
    volunteerHours: number | null;
    donationsTotalCents: number | null;
    memberTenureAvgDays: number | null;
  };
  trails: { slug: string; name: string; region: string | null; state: string | null }[];
  topRiders: { userId: string; email: string; goingCount: number; maybeCount: number }[];
}

export function getOrgAnalytics(idOrSlug: string) {
  return req<OrgAnalytics>(
    "GET",
    `/api/v1/admin/orgs/${encodeURIComponent(idOrSlug)}/analytics`
  );
}

/** Platform-wide rollup for the curator launchpad. */
export interface PlatformAnalytics {
  metrics: {
    orgsCount: number;
    trailsCount: number;
    eventsCount: number;
    eventsCancelled: number;
    eventsArchived: number;
    rsvpTotals: { going: number; maybe: number; notGoing: number };
    usersCount: number;
    crewCount: number;
    attendanceTotal: number | null;
    volunteerHours: number | null;
    donationsTotalCents: number | null;
  };
  topOrgs: {
    id: string;
    slug: string;
    name: string;
    logoUrl: string | null;
    trailCount: number;
    eventCount: number;
  }[];
}

export function getPlatformAnalytics() {
  return req<PlatformAnalytics>("GET", "/api/v1/curator/analytics");
}

export function deleteTrail(slug: string) {
  return req<{ ok: true; deletedSlug: string }>(
    "DELETE",
    `/api/v1/curator/trails/${slug}`
  );
}

export interface ClassifyResult {
  ok: true;
  slug: string;
  status: "open" | "closed" | "partially_open" | "unknown" | null;
  summary: string | null;
  source: "cron" | "admin" | null;
  action: "skipped" | "claude_called" | "error";
}

export function classifyTrail(slug: string) {
  return req<ClassifyResult>(
    "POST",
    `/api/v1/curator/trails/${slug}/classify`
  );
}

// ---------------------------------------------------------------------------
// Curator — trail admins
// ---------------------------------------------------------------------------

export interface TrailAdmin {
  userId: string;
  email: string;
  /** Per-trail membership role (owner/steward/member). After Phase B1
   *  backfill, every existing admin reads as `owner`. */
  role: MembershipRole;
  /** Platform-level role (user/admin/curator). Kept around so a
   *  curator-managed UI can show "this person is also a curator". */
  platformRole: Role;
  grantedAt: number;
  grantedBy: string | null;
}

export function listTrailAdmins(slug: string) {
  return req<{ items: TrailAdmin[] }>(
    "GET",
    `/api/v1/curator/trails/${slug}/admins`
  );
}

export function grantTrailAdmin(slug: string, email: string) {
  return req<{ ok: true; userId: string; email: string }>(
    "POST",
    `/api/v1/curator/trails/${slug}/admins`,
    { email }
  );
}

export function revokeTrailAdmin(slug: string, userId: string) {
  return req<{ ok: true; removed: number }>(
    "DELETE",
    `/api/v1/curator/trails/${slug}/admins/${encodeURIComponent(userId)}`
  );
}

// ---------------------------------------------------------------------------
// Curator — users
// ---------------------------------------------------------------------------

export interface UserRow {
  id: string;
  email: string;
  role: Role;
  /** Composed display name (first + last), or null when never set. */
  name: string | null;
  firstName: string | null;
  lastName: string | null;
  createdAt: number;
  lastSeenAt: number | null;
}

export function listUsers(q?: string) {
  const suffix = q ? `?q=${encodeURIComponent(q)}` : "";
  return req<{ items: UserRow[] }>("GET", `/api/v1/curator/users${suffix}`);
}

export function setUserRole(userId: string, role: Role) {
  return req<{ ok: true; previousRole: Role; newRole: Role }>(
    "PUT",
    `/api/v1/curator/users/${encodeURIComponent(userId)}/role`,
    { role }
  );
}

export { ApiError };
